//Appstle Subscription API

async function fetchWithTimeout(url, opts = {}, timeout = 15000, externalSignal) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  let onExternalAbort;
  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(id);
      controller.abort(externalSignal.reason);
    } else {
      onExternalAbort = () => controller.abort(externalSignal.reason);
      externalSignal.addEventListener('abort', onExternalAbort, { once: true });
    }
  }
  try {
    return await fetch(url, { ...opts, signal: controller.signal });
  } finally {
    clearTimeout(id);
    if (externalSignal && onExternalAbort) externalSignal.removeEventListener('abort', onExternalAbort);
  }
}

const basURL = "https://funkyfood.com.au/apps/subscriptions/cp/api";


const extractNoteValue = (note) => {
  if (!note || typeof note !== 'string' || !note.trim()) {
    return {};
  }
  const parts = note.split(",").map((part) => part.trim()).filter(Boolean) || [];
  const result = {};
  parts.forEach((part) => {
    const colonIndex = part?.indexOf(":");
    if (colonIndex === -1 || colonIndex === undefined) return;
    const key = part.substring(0, colonIndex);
    const value = part.substring(colonIndex + 1);
    if (key) {
      result[key.trim()] = value?.trim();
    }
  });
  return result;
};

function formatAddressText(cartAddress) {
  return `${cartAddress?.city || ""} ${cartAddress?.province || ""} ${
    cartAddress?.zip || ""
  }`.trim();
}

function searchSuburbUsingAddress(address) {
  return new Promise((resolve, reject) => {
    if (!google || !google.maps || !google.maps.places) {
      reject(new Error("Google Maps API is not loaded."));
      return;
    }
    const service = new google.maps.places.PlacesService(
      document.createElement("div")
    );
    service.textSearch(
      { query: address, type: "locality" },
      (results, status) => {
        // Google place/textsearch API called
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          results.length > 0
        ) {
          resolve(results[0].name); // Resolving with the first result
        } else {
          const errorMessage = `Error: ${status}. No results found.`;
          reject(new Error(errorMessage));
        }
      }
    );
  });
}

const getSuburbStateUsingAddress = async (orderNote, deliveryAddress) => {
  const cartAddress = deliveryAddress;
  let suburb = orderNote?.["Suburb Code"];
  // Prefer the structured `city` field from Shopify's address object.
  // Was calling Google Places textSearch on `formatAddressText(cartAddress)`
  // to derive this — redundant since Shopify already stores the suburb
  // name in `city`, and expensive (~$32/1000 calls, dominant driver of
  // Google Cloud spend). Preserved orderNote fallback for edge cases
  // where the contract address has no city populated.
  if (cartAddress?.city) suburb = cartAddress.city;
  return suburb;
};

const getDeliveryDateFromBilling = (billingDate, zone) => {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const deliveryIndex = days.findIndex((day) => zone?.day?.includes(day));
  const checkDay = days.findIndex((day_2) => zone?.day_2?.includes(day_2));

  let deliveryDate = null;
  if (deliveryIndex !== -1) {
    let { formattedDate } = getDeliveryDate(
      deliveryIndex,
      checkDay,
      new Date(billingDate)
    );
    deliveryDate = formattedDate;
  }
  return deliveryDate;
};


// Consolidated fetchCurrentSubscription (SUB-064)
// Options:
//   lookupSuburb, mergeLocalCart — legacy behavior flags
//   maxAgeMs — if >0, return sessionStorage cache `sub_<id>` when fresh
//   rawResultPromise — if provided, await this for the raw API payload instead
//     of making our own fetch (used by the inline preload script on shop-now
//     edit to parallelize the API call with HTML parse)
// maxAgeMs is skipped when lookupSuburb/mergeLocalCart are set, since those
// have side effects beyond the return value that the cached payload doesn't
// capture.
const fetchCurrentSubscription = async (token, subscriptionId, options = {}) => {
  const { lookupSuburb = false, mergeLocalCart = false, maxAgeMs = 0, rawResultPromise = null } = options;
  const cacheKey = `sub_${subscriptionId}`;

  if (maxAgeMs > 0 && !lookupSuburb && !mergeLocalCart) {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Date.now() - parsed.ts < maxAgeMs && Array.isArray(parsed.extractedData)) {
          // Re-hydrate downstream dependency
          if (parsed.deliverySuburbObj) {
            localStorage.setItem("deliverySuburbObj", JSON.stringify(parsed.deliverySuburbObj));
          }
          return parsed.extractedData;
        }
      }
    } catch (e) { /* private mode / JSON error — fall through to fetch */ }
  }

  const url = `${basURL}/subscription-contracts/contract-external/${subscriptionId}?isExternal=${true}&token=${token}`;
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  };

  try {
    let result;
    if (rawResultPromise) {
      // Inline preload path: use pre-fetched API response, skip our fetch.
      // If the preloaded promise rejected, fall back to our own fetch.
      try {
        result = await rawResultPromise;
      } catch (e) {
        let pending = null;
        try { pending = JSON.parse(localStorage.getItem('pendingSubscriptionUpdate') || 'null'); } catch (_) {}
        if (pending?.attributesHandledByWorker && String(pending.subscriptionId) === String(subscriptionId)) throw e;
        console.warn("rawResultPromise rejected, falling back to fresh fetch:", e);
        const response = await fetchWithTimeout(url, requestOptions);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        result = await response.json();
      }
    } else {
      const response = await fetchWithTimeout(url, requestOptions);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      result = await response.json();
    }
    const orderNote = extractNoteValue(result?.note);
    const cartAddress = result?.deliveryMethod?.address;

    // Suburb resolution: from address lookup or order note fallback
    let suburb = orderNote?.["Suburb Code"];
    let suburbState = orderNote?.["State"] || cartAddress?.provinceCode || "";
    if (lookupSuburb) {
      localStorage.setItem("currentSubscription", JSON.stringify(result));
      // Prefer the structured `city` field (same rationale as
      // getSuburbStateUsingAddress above — avoids a Google Places
      // textSearch call per subscription-edit page load).
      if (cartAddress?.city) suburb = cartAddress.city;
      suburbState = cartAddress?.provinceCode || suburbState;
    }

    const deliverySuburbObj = {
      suburbCodeValue: suburb || "",
      suburbStateValue: suburbState,
      deliveryAddress: cartAddress,
      discountItem: result?.discounts?.nodes,
      deliveryPrice: result?.deliveryPrice?.amount,
    };

    const cart = mergeLocalCart ? parseLocalStorageArray("localCart") : null;

    const extractedData = result?.lines.nodes?.map((node) => {
      const variantId = parseInt(node?.variantId?.split("/")?.pop(), 10);
      const productId = parseInt(node?.productId?.split("/")?.pop(), 10);
      const rawSellingPlan = parseInt(node?.sellingPlanId?.toString()?.split("/")?.pop(), 10);
      const properties = node?.customAttributes?.reduce((acc, attribute) => {
        acc[attribute?.key] = attribute?.value?.trim();
        return acc;
      }, {});

      // Appstle returns sellingPlanId = null for one-time items, so the parseInt
      // above gives NaN — which then fails the downstream `sellingPlan === "none"`
      // filter used to separate one-time from subscription items in the cart.
      // Detect one-time items via the `_appstle-one-time-product` custom attribute
      // and explicitly mark them so localCart preserves the distinction across
      // page navigations and submissions.
      const isOneTime = properties?.["_appstle-one-time-product"] === "true";
      const sellingPlan = isOneTime ? "none" : rawSellingPlan;

      const cartItem = mergeLocalCart ? cart?.find((c) => c.variantId === variantId) : null;

      return {
        ...(cartItem || {}),
        variantId,
        quantity: node.quantity,
        lineId: node.id,
        sellingPlan,
        sellingPlanName: node?.sellingPlanName || '',
        // Discounted subscriptions can return currentPrice.amount = null
        // with the discounted line total living on lineDiscountedPrice —
        // divide back out to a per-unit price so downstream cart totals
        // (common-theme.js getCartPricing + floating button _totalPrice)
        // don't sum to $0. Falls back to 0 only when neither field carries
        // a value.
        price: (function () {
          var cp = parseFloat(node?.currentPrice?.amount);
          if (!isNaN(cp) && cp > 0) return cp;
          var ldp = parseFloat(node?.lineDiscountedPrice?.amount);
          var qty = Number(node?.quantity) || 1;
          if (!isNaN(ldp) && qty > 0) return ldp / qty;
          return 0;
        })(),
        productId,
        productName: node?.title,
        productImage: node?.variantImage?.url ?? node?.variantImage?.transformedSrc,
        productPrice: node?.lineDiscountedPrice?.amount,
        isSuburbAvailable: true,
        discountItem: result?.discounts?.nodes,
        deliveryPrice: result?.deliveryPrice?.amount,
        properties,
        suburbCodeValue: suburb || "",
        suburbStateValue: suburbState,
        deliveryAddress: cartAddress,
        nextBillingDate: result?.nextBillingDate,
      };
    });

    if (mergeLocalCart) {
      localStorage.setItem("localCart", JSON.stringify(extractedData));
    }
    localStorage.setItem("deliverySuburbObj", JSON.stringify(deliverySuburbObj));

    // Write sessionStorage cache for this subscription — read back by callers
    // passing { maxAgeMs }, and by the inline preload script on shop-now edit.
    // Skip when side-effect-only options ran (lookupSuburb) since a subsequent
    // maxAgeMs caller wouldn't re-run the localStorage writes we just did.
    if (!lookupSuburb) {
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          ts: Date.now(),
          extractedData,
          deliverySuburbObj,
        }));
      } catch (e) { /* quota — non-fatal */ }
    }

    return extractedData;
  } catch (error) {
    console.error("Error fetching subscription contract:", error);
    return [];
  }
};

const editSubscriptionNewV3 = async (payload, token) => {
  const baseUrl = `${basURL}/subscription-contract-details/replace-variants-v3`;
  const params = {
    token,
  };

  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    body: JSON.stringify(payload),
    method: "POST",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error updating subscription line item:", error);
    throw new Error(`Updating subscription contract:: error occurred`);
  }
};

const updateSubscriptionItem = async (
  token,
  contractId,
  lineId,
  quantity,
  variantId
) => {
  const params = {
    contractId,
    lineId,
    quantity,
    variantId: `gid://shopify/ProductVariant/${variantId}`,
    isExternal: true,
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts-update-line-item-quantity`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;
  const requestOptions = {
    method: "PUT",
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error updating subscription line item:", error);
    throw error;
  }
};

const addSubscriptionItem = async (token, contractId, quantity, variantId,isOneTimeProduct = false) => {
  const params = {
    contractId,
    quantity,
    variantId,
    token,
  };

  const baseUrl = `${basURL}/v2/subscription-contracts-add-line-item`;
  const queryParams = new URLSearchParams(params).toString();
  let url;
  if(isOneTimeProduct){
    url = `${baseUrl}?${queryParams}&isOneTimeProduct=true`;
  }else{
    url = `${baseUrl}?${queryParams}`;
  }
  const requestOptions = {
    method: "PUT",
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error updating subscription line item:", error);
    throw error;
  }
};

const deleteSubscriptionItem = async (token, contractId, lineId) => {
  const params = {
    contractId,
    lineId,
    isExternal: true,
    removeDiscount: false,
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts-remove-line-item`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;
  const requestOptions = {
    method: "PUT",
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error updating subscription line item:", error);
    throw error;
  }
};

const getAllContracts = async (token, cursor, hasNextPage = false) => {
  const params = {
    token,
    ...(hasNextPage && cursor && { cursor }),
  };

  const baseUrl = `${basURL}/subscription-customers`;

  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;
  const requestOptions = {
    method: "GET",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error updating subscription line item:", error);
    throw error;
  }
};

const getAllContractDetails = async (token) => {
  const params = {
    isExternal: true,
    token,
  };

  const baseUrl = `${basURL}/subscription-customers-detail/valid/${token}`;

  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;
  const requestOptions = {
    method: "GET",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error updating subscription line item:", error);
    throw error;
  }
};


const getContract = async (token, subscription_id) => {
  const params = {
    isExternal: true,
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts/contract-external/${subscription_id}`;

  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;
  const requestOptions = {
    method: "GET",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error updating subscription line item:", error);
    throw error;
  }
};



const getUpcomingBillings = async (token, contractId) => {
  const params = {
    token,
    contractId,
  };

  const baseUrl = `${basURL}/subscription-billing-attempts/top-orders`;

  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;
  const requestOptions = {
    method: "GET",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error updating subscription line item:", error);
    throw error;
  }
};

const getHistory = async (token, contractId, page = 0, size = 10) => {
  const params = {
    token,
    contractId,
    page,
    size,
  };

  const baseUrl = `${basURL}/subscription-billing-attempts/past-orders`;

  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;
  const requestOptions = {
    method: "GET",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error updating subscription line item:", error);
    throw error;
  }
};


const updateNextBillingDate = async (token, subscriptionContractId, dateString) => {
  const params = {
    contractId: subscriptionContractId,
    token,
    rescheduleFutureOrder: true,
    nextBillingDate: dateString
  };

  const baseUrl = `${basURL}/subscription-contracts-update-billing-date/`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions, 60000);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error Skipping subscription line item:", error);
    throw error;
  }
};


const skipUpcomingOrderApi = async (token, subscriptionContractId) => {
  const params = {
    subscriptionContractId,
    token,
  };

  const baseUrl = `${basURL}/subscription-billing-attempts/skip-upcoming-order/`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error Skipping subscription line item:", error);
    throw error;
  }
};

const skipCurrentOrderApi = async (orderId, token, subscriptionContractId, signal) => {
  const params = {
    isPrepaid: false,
    subscriptionContractId,
    isExternal: true,
    token,
  };

  const baseUrl = `${basURL}/subscription-billing-attempts/skip-order/${orderId}`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions, 8000, signal);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    if (error?.name !== 'AbortError' && !signal?.aborted) {
      console.error("Error Skipping subscription line item:", error);
    }
    throw error;
  }
};

const unSkipCurrentOrderApi = async (
  orderId,
  token,
  subscriptionContractId,
  signal
) => {
  const params = {
    subscriptionContractId,
    token,
  };

  const baseUrl = `${basURL}/subscription-billing-attempts/unskip-order/${orderId}`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions, 8000, signal);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    if (error?.name !== 'AbortError' && !signal?.aborted) {
      console.error("Error Skipping subscription line item:", error);
    }
    throw error;
  }
};

const applyDiscountCode = async (
  discountCode,
  token,
  subscriptionContractId
) => {
  const params = {
    contractId: subscriptionContractId,
    discountCode,
    isPrepaid: false,
    isExternal: true,
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts-apply-discount`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions, 60000);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error applying discount", error);
    throw error;
  }
};

const removeDiscountCode = async (
  discountId,
  token,
  subscriptionContractId
) => {
  const params = {
    contractId: subscriptionContractId,
    discountId,
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts-remove-discount`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions, 60000);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error applying discount", error);
    throw error;
  }
};

const updateCustomerAddress = async (
  payload,
  token,
  subscriptionContractId
) => {
  const params = {
    contractId: subscriptionContractId,
    isExternal: true,
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts-update-shipping-address`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
    body: JSON.stringify(payload),
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error applying discount", error);
    throw error;
  }
};

const updateNoteAttributesApi = async (
  token,
  orderNoteBody
) => {
  const params = {
    overwriteExistingAttributes: false,
    token,
  };

  const baseUrl = `${basURL}/update-custom-note-attributes/`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    body: JSON.stringify(orderNoteBody),
    method: "POST",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions, 60000);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error Updating Note Attributes", error);
    throw error;
  }
};


const updateOrderNoteApi = async (
  token,
  subscriptionContractId,
  orderNote = null
) => {
  const params = {
    orderNote: orderNote,
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts-update-order-note/${subscriptionContractId}`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error Updating Note", error);
    throw error;
  }
};

const updateCustomerStatusApi = async (
  status,
  token,
  subscriptionContractId,
  pauseReason = null
) => {
  const params = {
    contractId: subscriptionContractId,
    status,
    isExternal: true,
    ...(pauseReason && { pauseReason }),
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts-update-status`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error Updating Status", error);
    throw error;
  }
};

const cancelCustomerSubsApi = async (
  token,
  subscriptionContractId,
  feedback,
  reason,
) => {
  const params = {
    isExternal: true,
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts/${subscriptionContractId}`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "DELETE",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    // const result = await response.json();
    // return result;
    return;
  } catch (error) {
    console.error("Error Canceling Status", error);
    throw error;
  }
};

const getAllBillingPlans = async (token, productIds) => {
  const params = {
    token,
  };

  const baseUrl = `${basURL}/data/v2/products-selling-plans`;

  const productIdsString =
    productIds?.map((id) => `productIds=${id}`)?.join("&") || "productIds=";
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}&${productIdsString}`;
  const requestOptions = {
    method: "GET",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error Getting Selling Plans:", error);
    throw error;
  }
};

const getAllSellingPlans = async (token) => {
  const params = {
    token,
  };

  const baseUrl = `${basURL}/subscription-groups/all-selling-plans`;

  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;
  const requestOptions = {
    method: "GET",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error Getting Selling Plans:", error);
    throw error;
  }
};



const updateCustomerFrequency = async (
  sellingPlanId,
  token,
  subscriptionContractId
) => {
  const params = {
    contractId: subscriptionContractId,
    sellingPlanId,
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts-update-frequency-by-selling-plan`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions, 60000);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error Update frequency", error);
    throw error;
  }
};

const getPaymentMethods = async (token) => {
  const params = {
    token,
  };

  const baseUrl = `${basURL}/subscription-contract-details/shopify/customer/payment-methods`;

  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;
  const requestOptions = {
    method: "GET",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error Getting Selling Plans:", error);
    throw error;
  }
};

const getAllAddress = async (token) => {
  const params = {
    token,
  };

  const baseUrl = `${basURL}/v2/data/addresses`;

  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;
  const requestOptions = {
    method: "GET",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error Getting Selling Plans:", error);
    throw error;
  }
};

const updatePaymentMethod = async (
  paymentMethodId,
  token,
  subscriptionContractId
) => {
  const params = {
    contractId: subscriptionContractId,
    paymentMethodId,
    isExternal: true,
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts-update-existing-payment-method`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error Update Payment", error);
    throw error;
  }
};

const changePaymentMethod = async (token, subscriptionContractId) => {
  const params = {
    contractId: subscriptionContractId,
    isExternal: true,
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts-update-payment-method`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorDetails = await response.json();
      console.error("API error details:", errorDetails);
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  } catch (error) {
    console.error("Error Update Payment", error);
    throw error;
  }
};

const updateProductAttributes = async (
  subscriptionContractId,
  token,
  lineId,
  payload
) => {
  const params = {
    contractId: subscriptionContractId,
    lineId,
    isExternal: true,
    token,
  };

  const baseUrl = `${basURL}/subscription-contracts-update-line-item-attributes`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "PUT",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  };

  // 60s timeout (default 15s too tight: Appstle occasionally spikes >15s).
  // Retry once on failure — editExclude fires many in parallel, one flaky
  // request shouldn't fail the whole exclusions update.
  const doFetch = async () => {
    const response = await fetchWithTimeout(url, requestOptions, 60000);
    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try { errorDetails = JSON.parse(errorText); } catch(e) { errorDetails = {}; }
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const resultText = await response.text();
    return resultText ? JSON.parse(resultText) : {};
  };

  try {
    return await doFetch();
  } catch (error) {
    console.warn("updateProductAttributes: first attempt failed, retrying once", error?.message);
    try {
      await new Promise(r => setTimeout(r, 500));
      return await doFetch();
    } catch (retryError) {
      console.error("Error Update Product Attributes (retry also failed)", retryError);
      throw retryError;
    }
  }
};

const LoginScreenHTML = (
  msg = "Please log in to access the customer portal."
) => {
  return `
        <div 
          style="
            width:100%; 
            display: flex; 
            flex-direction: column; 
            justify-content: center; 
            align-items: center;
            padding: 10px;
            border: 1px solid #e5e7eb;
            border-radius: 28px;
            margin-top: 10px;
          "
        >
          <h3 style="text-align:center;">
            ${msg}
          </h3>
          <button onclick="redirectToLoginScreen()" class="edit-box-btn-customer" style="cursor: pointer; margin-bottom:1em;">
            Login
          </button>
        </div>`;
};

const magicLinkExpiredComponent = (token) => {
  return `
    <div 
      style="
        width:100%; 
        display: flex; 
        flex-direction: column; 
        justify-content: center; 
        align-items: center;
        padding: 10px;
        border: 1px solid #e5e7eb;
        border-radius: 28px;
        margin-top: 10px;
      "
    >
      
      <h3 style="text-align:center;">
        Retrieve Magic Link
      </h3>
      <p style="margin:0;text-align:center;" >Your magic link has expired. Enter your email to get a new link.</p>
      <input placeholder="example@gmail.com" type="text" id="customer-email-expired" name="customer-email-expired" class="input-style" style="width: 100%; margin:10px 0px;width: 100%; max-width:350px"/>
      <span id="customer-magic-link-msg" style="color: grey;font-size: 13px;  margin-bottom: 10px;"></span>
      <button onclick="sentEmailWithMagicLink('${token}')" id="customer-send-magic-button" class="edit-box-btn-customer" style="cursor:pointer; margin-bottom:1em;">
        Email Magic Link
      </button>
    </div>`;
};

const sentEmailWithMagicLink = async (token) => {
  const emailInput = document.getElementById("customer-email-expired");
  const messageBox = document.getElementById("customer-magic-link-msg");
  const sendBtn = document.getElementById("customer-send-magic-button");

  if (!emailInput || !messageBox || !sendBtn) return;

  const email = emailInput?.value?.trim();
  messageBox.innerHTML = "";

  if (!email) {
    messageBox.innerHTML = "Email is required";
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    messageBox.innerHTML = "Invalid email format";
    return;
  }

  sendBtn.innerHTML = `
    <div class="loading-overlay__spinner">
      <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
      </svg>
    </div>`;

  const params = {
    email,
    token,
    shop: "funkyfood2.myshopify.com",
  };

  const baseUrl = `${basURL}/subscription-contracts-email-magic-link`;
  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryParams}`;

  const requestOptions = {
    method: "GET",
    headers: {
      accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await fetchWithTimeout(url, requestOptions);
    if (!response.ok) {
      const errorDetails = await response.json();
      messageBox.innerHTML = errorDetails?.message || "Unknown error";
      throw new Error(
        `Error ${response?.status}: ${
          errorDetails?.title || response?.statusText || "Unknown error"
        }`
      );
    }
    const result = await response.text();
    messageBox.innerHTML = result?.toString() || "";
    return result;
  } catch (error) {
    console.error("Error Sending Magic Link Email", error);
    throw error;
  } finally {
    sendBtn.innerHTML = "Email Magic Link";
  }
};


// Permanent exclusions belong to subscription line attributes, not the delayed box mirror.
window.FFSubscriptionPreferences = {
  read: function (contract) {
    var lines = Array.isArray(contract.lines) ? contract.lines : (contract.lines && contract.lines.nodes) || [];
    var rows = lines.flatMap(function(line){return line.customAttributes || [];}).filter(function(a){return a.key === 'Excluding';});
    if (!rows.length) rows = (contract.customAttributes || []).filter(function(a){return a.key === 'Excluding';});
    return Array.from(new Set(rows.flatMap(function(a){return String(a.value || '').split(',').map(function(v){return v.trim();}).filter(Boolean);})));
  },
  load: async function (token, id) {
    if (!token || !id) throw new Error('Missing subscription details');
    var pending;
    try { pending = JSON.parse(localStorage.getItem('pendingSubscriptionUpdate') || 'null'); } catch (_) {}
    if (pending && String(pending.subscriptionId) === String(id) && pending.taskId && pending.attributesHandledByWorker) {
      var base = window.themeApiConfig?.workerUrl || 'https://funkyfood-subscription-worker.janfemac1122.workers.dev';
      var done = false;
      for (var i=0;i<30;i++) {
        var response = await fetch(base+'/api/task-status/'+encodeURIComponent(pending.taskId),{cache:'no-store'});
        if (!response.ok) throw new Error('Unable to verify your subscription update');
        var state = await response.json();
        if (state.status === 'completed') { done=true; break; }
        if (state.status === 'failed' || state.status === 'cancelled') throw new Error('Your subscription update was not saved. Please try again.');
        await new Promise(function(resolve){setTimeout(resolve,1000);});
      }
      if (!done) throw new Error('Your subscription update is still processing. Please try again shortly.');
    }
    var result = await fetch(basURL+'/subscription-contracts/contract-external/'+encodeURIComponent(id)+'?isExternal=true&token='+encodeURIComponent(token),{cache:'no-store',headers:{'Content-Type':'application/json'}});
    if (!result.ok) throw new Error('Unable to load subscription exclusions');
    return result.json();
  },
  invalidate: function(id) {
    try {
      sessionStorage.removeItem('sub_'+id);
      Object.keys(sessionStorage).filter(function(key){return key.indexOf('portal_cache_')===0;}).forEach(function(key){sessionStorage.removeItem(key);});
    } catch (_) {}
  },
  save: async function (token,id,names,lineIds) {
    var contract = await this.load(token,id);
    var lines = Array.isArray(contract.lines) ? contract.lines : (contract.lines && contract.lines.nodes) || [];
    var selected = lines.filter(function(line){return (lineIds || []).includes(line.id);});
    if (!selected.length) selected = lines.filter(function(line){return (line.customAttributes || []).some(function(a){return a.key === 'Excluding' || a.key === 'Box items';});});
    if (!selected.length) throw new Error('No box line found for exclusions');
    await Promise.all(selected.map(function(line){
      var attrs = (line.customAttributes || []).filter(function(a){return a.key !== 'Excluding';});
      attrs.push({key:'Excluding',value:names.join(', ')});
      return updateProductAttributes(id,token,line.id,attrs);
    }));
    this.invalidate(id);
    return contract;
  }
};
